import { Link, useNavigate } from "react-router";
import { DashboardCard } from "../molecules/DashboardCard";
import { DueDateCell } from "../molecules/DueDateCell";
import {
  hasWorkflowData,
  type DocumentReleaseSummaryWithWorkflow,
} from "~/api/types";
import { dateDiff } from "~/helpers/date";
import { useTranslation } from "react-i18next";
import NoResult from "../molecules/NoResult";
import SimpleDataTable, {
  type TableColumn,
} from "../molecules/SimpleDataTable";
import { CenterSpinner } from "../atoms/spinner";
import { Button } from "~/atoms/button";
import { useDocumentReleases } from "~/api/v2/useDocumentReleases";
import { DocumentArtifactStatus } from "~/config/types";

const UPCOMING_REVIEW_DAYS_THRESHOLD = 90;

const UpcomingWorkflowsContainer = () => {
  const { t } = useTranslation();

  const columns: TableColumn<DocumentReleaseSummaryWithWorkflow>[] = [
    {
      header: t(($) => $.dashboard.column.title),
      compareFn: (a, b) => a.artifact.title.localeCompare(b.artifact.title),
      cell: (release) => (
        <div className="min-w-0">
          <span className="block truncate" title={release.artifact.title}>
            {release.artifact.title}
          </span>
        </div>
      ),
      cellClassName: "w-full max-w-0",
      tableHeadClassName: "w-full",
    },
    {
      header: t(($) => $.commons.document.reviewFrom),
      defaultSort: true,
      compareFn: (a, b) =>
        dateDiff(a.artifact.reviewFrom, b.artifact.reviewFrom),
      cell: (v) => <DueDateCell dueDate={v.artifact.reviewFrom} />,
      cellClassName: "whitespace-nowrap",
      tableHeadClassName: "whitespace-nowrap",
    },
    {
      header: t(($) => $.commons.document.reviewTo),
      compareFn: (a, b) => dateDiff(a.artifact.reviewTo, b.artifact.reviewTo),
      cell: (v) => <DueDateCell dueDate={v.artifact.reviewTo} />,
      cellClassName: "whitespace-nowrap",
      tableHeadClassName: "whitespace-nowrap",
    },
  ];
  const navigate = useNavigate();
  const { data: releases, isLoading } = useDocumentReleases({
    statuses: [DocumentArtifactStatus.draft],
    includeWorkflowData: true,
  });

  const upcomingWorkflows = releases
    ?.filter(hasWorkflowData)
    .filter(
      (release) =>
        !release.artifact.workflow.isOngoing &&
        release.artifact.status === DocumentArtifactStatus.draft &&
        release.artifact.reviewFrom &&
        dateDiff(release.artifact.reviewFrom, new Date(), "day") <=
          UPCOMING_REVIEW_DAYS_THRESHOLD,
    );

  const title = t(($) => $.dashboard.upcoming.workflows);

  if (isLoading) {
    return (
      <DashboardCard title={title} className="h-80">
        <CenterSpinner />
      </DashboardCard>
    );
  }

  if (upcomingWorkflows?.length === 0) {
    return (
      <DashboardCard title={title}>
        <NoResult className="h-full" />
      </DashboardCard>
    );
  }

  const button = (
    <Button variant="link" asChild>
      <Link to="workflows?tab=upcoming">
        {t(($) => $.commons.buttons.seeAll)}
      </Link>
    </Button>
  );

  return (
    <DashboardCard title={title} button={button}>
      <SimpleDataTable
        data={upcomingWorkflows ?? []}
        columns={columns}
        onClickRow={(i) => navigate(`/documents/${i.document.id}?tab=workflow`)}
        wrapperClassName="max-h-95"
      />
    </DashboardCard>
  );
};

export default UpcomingWorkflowsContainer;
